import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { trackEvent } from "@/lib/analytics";
import { useDownload } from "@/lib/downloads";

const INTRO =
  "This is the difference between using Poly Grid as a nicer terminal and running it as a fleet. Eight habits that keep every pane's tokens working for you instead of idling, waiting, or bleeding context — most of them a one-time toggle.";

const SECTIONS = [
  {
    h2: "Turn on Terminal Hooks first",
    body: "Auto-Compact, the context meter, live agent status, and the triage strip all read real state from your agent instead of guessing from terminal output — but only once hooks are installed. Do this before anything else below. Set up Claude Code, Codex, and opencode separately from the app menu; hooks attach to sessions started after install, so restart anything already running.",
    code: "# Menu → Set up Claude Code status hooks… (repeat for Codex / opencode)",
  },
  {
    h2: "Cap context with Auto-Compact",
    body: "Every pane that keeps talking past ~140k tokens is spending more of every turn re-reading its own history than doing new work. Turn on Auto-compact… → threshold 140000 (the default) and Poly Grid sends /compact at a clean turn boundary the moment a pane crosses it — no watching the meter, no mid-thought interruption. This is the single highest-leverage toggle for token spend.",
    code: "# Menu → Auto-compact…\nAuto-run /compact when a pane crosses the threshold: ON\nThreshold: 140000",
  },
  {
    h2: "Watch the meter before it decides for you",
    body: "The context window meter (`82k ctx` in the pane header) is free and always on for Claude Code panes. On a task you know is about to get read-heavy — a big refactor, a wide search — glance at the meter and /compact manually before Auto-Compact's threshold forces the timing. Cheap insurance against losing a turn's worth of momentum to a mid-task compact.",
  },
  {
    h2: "One space per context, not one grid for everything",
    body: "Up to 16 spaces, each with its own panes, layout, and broadcast group. The moment you're holding two unrelated jobs in your head — client work and a side project, or the feature you're building and the incident that just paged — split them into separate spaces instead of scrolling one crowded grid. Switch from the Spaces ▾ dropdown, and nothing in space A can distract or get broadcast into space B.",
  },
  {
    h2: "Broadcast instead of retyping",
    body: "Typing the same prompt into three panes is three times the typing and three chances to phrase it slightly differently. ⌥-click pane titles to build a broadcast group (or ⇧⌘B to toggle broadcast mode), then type once. Rolling a dependency bump across a microservice cluster, or asking the same question of three agents working the same bug from different angles, becomes one send instead of three.",
  },
  {
    h2: "Never let an agent sit idle",
    body: "An agent that finished its turn and is waiting on you is a pane doing nothing until you notice. Queue the next few prompts in a pane's work queue and they feed in automatically, one per turn, the moment it goes idle — no double-feed. Line up a backlog before a meeting or before bed and the pane keeps moving without you.",
  },
  {
    h2: "Race N approaches with Worktree Fan-out",
    body: "Some tasks are worth spending tokens on more than once. Fan out a task to N worktrees… spins up N isolated git-worktree panes on fresh branches, each auto-running the same prompt — so instead of guessing which approach an agent should take, you read N diffs side by side and keep the one that won. Partial-failure tolerant, so one bad branch doesn't sink the batch.",
  },
  {
    h2: "Supervise the fleet, not each pane",
    body: "Eyeballs don't need to scale 1:1 with panes. Leave the triage strip watching — it surfaces a bubble the moment any pane needs you, across every space, and tells you which space to switch to. Sentinel's fleet drawer rolls every agent's live state and last summary into one view, and Standup copies a paste-ready end-of-day digest. Run ten agents, supervise like it's one.",
  },
];

export const TenXDev = () => {
  useDocumentMeta({
    title: "Become a 10x Dev — Poly Grid",
    description: INTRO,
    path: "/10x-dev",
  });

  const { primary } = useDownload();
  const heroLabel = primary.external ? "Download" : `Download — ${primary.label}`;

  return (
    <div className="relative min-h-screen bg-[var(--pg-bg)]">
      <Navbar />

      <section data-testid="ten-x-dev-page" className="border-b border-pg">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <Link
            to="/"
            data-testid="ten-x-dev-back-link"
            className="inline-flex items-center gap-2 tag-label text-[var(--pg-text-dim)] hover:text-white transition-colors mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to home
          </Link>

          <div className="tag-label text-cyan-accent">/// Guide</div>
          <h1 className="font-display mt-3 text-4xl lg:text-6xl font-black tracking-tighter uppercase">
            Become a 10x dev.
          </h1>

          <p className="mt-8 text-[16px] text-[var(--pg-text-dim)] leading-relaxed">
            {INTRO}
          </p>

          <div className="mt-12 space-y-10">
            {SECTIONS.map((s, i) => {
              const anchor = s.h2.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <section
                  key={i}
                  id={anchor}
                  className="scroll-mt-24"
                  data-testid={`ten-x-dev-section-${anchor}`}
                >
                  <h2 className="font-display text-xl lg:text-2xl font-bold tracking-tight">
                    <a
                      href={`#${anchor}`}
                      className="hover:text-cyan-accent transition-colors group"
                    >
                      <span className="text-cyan-accent mr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        #
                      </span>
                      {s.h2}
                    </a>
                  </h2>
                  <p className="mt-3 text-[15px] leading-relaxed text-[var(--pg-text-dim)]">
                    {s.body}
                  </p>
                  {s.code && (
                    <pre className="mt-4 bg-[#0a0a0a] border border-pg p-4 text-[13px] leading-relaxed font-mono text-[var(--pg-text-dim)] overflow-x-auto rounded-sm">
                      {s.code}
                    </pre>
                  )}
                </section>
              );
            })}
          </div>

          <div className="mt-16 pt-8 border-t border-pg flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div className="text-xs font-mono text-[var(--pg-text-muted)]">
              Questions? support@poly-grid.com
            </div>
            <Button
              asChild
              data-testid="ten-x-dev-cta-download"
              className="h-11 rounded-sm font-display font-bold tag-label text-black hover:opacity-90"
              style={{ backgroundColor: "var(--pg-cyan)" }}
            >
              <a
                href={primary.url}
                download={!primary.external}
                target={primary.external ? "_blank" : undefined}
                rel={primary.external ? "noopener noreferrer" : undefined}
                onClick={() =>
                  trackEvent("download_app", {
                    location: "ten_x_dev_page",
                    tier: "free",
                    platform: primary.platform,
                  })
                }
              >
                <Download className="w-4 h-4 mr-2" />
                {heroLabel}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TenXDev;
